import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Header from './components/layout/Header/Header';
import Login from './pages/Login/Login';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import Offer from './pages/Offer/Offer';
import AddNewOffer from './pages/Offer/AddNewOffer/AddNewOffer';
import EditOffer from './pages/Offer/EditOffer/EditOffer';
import VehicleColors from './pages/CMS/VehicleColors/VehicleColors';
import AddNewColor from './pages/CMS/VehicleColors/AddNewColor/AddNewColor';
import EditColor from './pages/CMS/VehicleColors/EditColor/EditColor';
import CityManagement from './pages/CMS/CityManagement/CityManagement';
import AddNewCity from './pages/CMS/CityManagement/AddNewCity/AddNewCity';
import EditCity from './pages/CMS/CityManagement/EditCity/EditCity';
import SparePartsVariants from './pages/SparePartsManagement/SparePartsVariants/SparePartsVariants';
import AddNewSparePartsVariants from './pages/SparePartsManagement/SparePartsVariants/AddNewSparePartsVariants/AddNewSparePartsVariants';
import EditSparePartsVariants from './pages/SparePartsManagement/SparePartsVariants/EditSparePartsVariants/EditSparePartsVariants';
import SparePartsPricing from './pages/SparePartsManagement/SparePartsPricing/SparePartsPricing';
import AddNewSparePartsPricing from './pages/SparePartsManagement/SparePartsPricing/AddNewSparePartsPricing/AddNewSparePartsPricing';
import EditSparePartsPricing from './pages/SparePartsManagement/SparePartsPricing/EditSparePartsPricing/EditSparePartsPricing';
import MaintenanceCalculator from './pages/CMS/MaintenanceCalculator/MaintenanceCalculator';
import AddNewCalculation from './pages/CMS/MaintenanceCalculator/AddNewCalculation/AddNewCalculation';
import EditCalculation from './pages/CMS/MaintenanceCalculator/EditCalculation/EditCalculation';
import TermsConditions from './pages/CMS/TermsConditions/TermsConditions';
import LocationManagement from './pages/CMS/LocationManagement/LocationManagement';
import AddNewLocation from './pages/CMS/LocationManagement/AddNewLocation/AddNewLocation';
import EditLocation from './pages/CMS/LocationManagement/EditLocation/EditLocation';
import UserRoles from './pages/UserRolesApproval/UserRoles/UserRoles';
import AddNewUser from './pages/UserRolesApproval/UserRoles/AddNewUser/AddNewUser';
import EditUserRole from './pages/UserRolesApproval/UserRoles/EditUserRole/EditUserRole';
import UserApproval from './pages/UserRolesApproval/UserApproval/UserApproval';
import SetHierarchy from './pages/UserRolesApproval/UserApproval/SetHierarchy';
import EditHierarchy from './pages/UserRolesApproval/UserApproval/EditHierarchy';
import ApprovalRequests from './pages/UserRolesApproval/UserApproval/ApprovalRequests';
import ApprovalTracking from './pages/UserRolesApproval/UserApproval/ApprovalTracking';
import VehicleManagement from './pages/VehicleManagement/VehicleManagement';
import AddNewVehicle from './pages/VehicleManagement/AddNewVehicle/AddNewVehicle';
import EditVehicle from './pages/VehicleManagement/EditVehicle/EditVehicle';
import BannerManagement from './pages/CMS/BannerManagement/BannerManagement';
import AddNewBanner from './pages/CMS/BannerManagement/AddNewBanner/AddNewBanner';
import EditBanner from './pages/CMS/BannerManagement/EditBanner/EditBanner';
import BlogManagement from './pages/CMS/BlogManagement/BlogManagement';
import AddNewBlog from './pages/CMS/BlogManagement/AddNewBlog/AddNewBlog';
import BlogView from './pages/CMS/BlogManagement/BlogView/BlogView';
import EditBlog from './pages/CMS/BlogManagement/EditBlog/EditBlog';
import OfferView from './pages/Offer/OfferView/OfferView';
import UTMBuilder from './pages/UTMBuilder/UTMBuilder';
import AddNewUTM from './pages/UTMBuilder/AddNewUTM/AddNewUTM';
import Dashboard from './pages/Dashboard/Dashboard';
import OfferPreview from './pages/Offer/OfferPreview/OfferPreview';
import ShipmentInfo from './pages/ShipmentInfo/ShipmentInfo';
import AccountDisabled from './pages/AccountDisabled/AccountDisabled';
import ViewVehicle from './pages/VehicleManagement/ViewVehicle/ViewVehicle';
import VehicleColorsManagement from './pages/VehicleManagement/VehicleColors/VehicleColors';
import VehicleVariantsPage from './pages/VehicleManagement/VehicleVariants/VehicleVariants';
import VehicleImagesPage from './pages/VehicleManagement/VehicleImages/VehicleImages';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, hasRole } = useAuth();
  
  if (loading) {
    return <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>;
  }
  
  // Redirect disabled users to account-disabled page
  if (isAuthenticated && hasRole('website-user')) {
    return <Navigate to="/account-disabled" />;
  }
  
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const AppRoutes = () => {
  const { isAuthenticated, loading, user, hasRole } = useAuth();

  if (loading) {
    return <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>;
  }

  // Check if user has website-user role and redirect to disabled page
  const isDisabledUser = isAuthenticated && hasRole('website-user');

  return (
    <>
      {isAuthenticated && !isDisabledUser && <Header />}
      <Routes>
        <Route
          path="/"
          element={
            isAuthenticated 
              ? (isDisabledUser ? <Navigate to="/account-disabled" /> : <Navigate to="/dashboard" />) 
              : <Login />
          }
        />
        <Route
          path="/login"
          element={isAuthenticated ? (isDisabledUser ? <Navigate to="/account-disabled" /> : <Navigate to="/dashboard" />) : <Login />}
        />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        
        {/* Account Disabled Page - Accessible only to authenticated users with restricted role */}
        <Route 
          path="/account-disabled" 
          element={isAuthenticated ? <AccountDisabled /> : <Navigate to="/login" />} 
        />
        
        <Route path="/dashboard" element={
          isAuthenticated 
            ? (isDisabledUser ? <Navigate to="/account-disabled" /> : <Dashboard />) 
            : <Navigate to="/login" />
        } />
       

        {/* Offer & Coupon Management */}
        <Route path="/offers" element={isAuthenticated ? <Offer /> : <Navigate to="/login" />} />
        <Route path="/offers/add-new" element={isAuthenticated ? <AddNewOffer /> : <Navigate to="/login" />} />
        <Route path="/offers/edit/:id" element={isAuthenticated ? <EditOffer /> : <Navigate to="/login" />} />
        <Route path="/offers/view/:id" element={isAuthenticated ? <OfferView /> : <Navigate to="/login" />} />
        <Route path="/utm-builder" element={isAuthenticated ? <UTMBuilder /> : <Navigate to="/login" />} />
        <Route path="/utm-builder/add-new" element={isAuthenticated ? <AddNewUTM /> : <Navigate to="/login" />} />
        <Route path="/offers/preview/:id" element={isAuthenticated ? <OfferPreview /> : <Navigate to="/login" />} />

        {/* Product Management
        <Route path="/vehicle-management" element={isAuthenticated ? <VehicleManagement /> : <Navigate to="/login" />} />
        <Route path="/vehicle-management/add-new" element={isAuthenticated ? <AddNewVehicle /> : <Navigate to="/login" />} />
        <Route path="/vehicle-management/edit/:id" element={isAuthenticated ? <EditVehicle /> : <Navigate to="/login" />} /> */}

        {/* Shipping Management */}
        <Route path="/shipping-info" element={isAuthenticated ? <ShipmentInfo /> : <Navigate to="/login" />} />

        {/* Vehicle Colors route for CMS
        <Route path="/vehicle-colors" element={isAuthenticated ? <VehicleColors /> : <Navigate to="/login" />} />
        <Route path="/vehicle-colors/add-new" element={isAuthenticated ? <AddNewColor /> : <Navigate to="/login" />} />
        <Route path="/cms/vehicle-colors/edit/:id" element={isAuthenticated ? <EditColor /> : <Navigate to="/login" />} /> */}

        {/* CMS Management */}
        <Route path="/cms/cities" element={isAuthenticated ? <CityManagement /> : <Navigate to="/login" />} />
        <Route path="/cms/cities/add-new" element={isAuthenticated ? <AddNewCity /> : <Navigate to="/login" />} />
        <Route path="/cms/cities/edit/:id" element={isAuthenticated ? <EditCity /> : <Navigate to="/login" />} />
        <Route path="/cms/maintenance-calculator" element={isAuthenticated ? <MaintenanceCalculator /> : <Navigate to="/login" />} />
        <Route path="/cms/maintenance-calculator/add-new" element={isAuthenticated ? <AddNewCalculation /> : <Navigate to="/login" />} />
        <Route path="/cms/maintenance-calculator/edit/:id" element={isAuthenticated ? <EditCalculation /> : <Navigate to="/login" />} />
        <Route path="/cms/terms-conditions" element={isAuthenticated ? <TermsConditions /> : <Navigate to="/login" />} />
        <Route path="/cms/location-management" element={isAuthenticated ? <LocationManagement /> : <Navigate to="/login" />} />
        <Route path="/cms/location-management/add-new" element={isAuthenticated ? <AddNewLocation /> : <Navigate to="/login" />} />
        <Route path="/cms/location-management/edit/:id" element={isAuthenticated ? <EditLocation /> : <Navigate to="/login" />} />
        <Route path="/cms/banner-management" element={isAuthenticated ? <BannerManagement /> : <Navigate to="/login" />} />
        <Route path="/cms/banner-management/add-new" element={isAuthenticated ? <AddNewBanner /> : <Navigate to="/login" />} />
        <Route path="/cms/banner-management/edit/:id" element={isAuthenticated ? <EditBanner /> : <Navigate to="/login" />} />
        <Route path="/cms/blog-management" element={isAuthenticated ? <BlogManagement /> : <Navigate to="/login" />} />
        <Route path="/cms/blog-management/add-new" element={isAuthenticated ? <AddNewBlog /> : <Navigate to="/login" />} />
        <Route path="/cms/blog-management/view/:id" element={isAuthenticated ? <BlogView /> : <Navigate to="/login" />} />
        <Route path="/cms/blog-management/edit/:id" element={isAuthenticated ? <EditBlog /> : <Navigate to="/login" />} />

        {/* Spare Parts Management */}
        <Route path="/spare-parts/variants" element={isAuthenticated ? <SparePartsVariants /> : <Navigate to="/login" />} />
        <Route path="/spare-parts/variants/add" element={isAuthenticated ? <AddNewSparePartsVariants /> : <Navigate to="/login" />} />
        <Route path="/spare-parts-variants/edit/:id" element={isAuthenticated ? <EditSparePartsVariants /> : <Navigate to="/login" />} />
        <Route path="/spare-parts-management/spare-parts-pricing" element={isAuthenticated ? <SparePartsPricing /> : <Navigate to="/login" />} />
        <Route path="/spare-parts-management/spare-parts-pricing/add" element={isAuthenticated ? <AddNewSparePartsPricing /> : <Navigate to="/login" />} />
        <Route path="/spare-parts-management/spare-parts-pricing/edit/:id" element={isAuthenticated ? <EditSparePartsPricing /> : <Navigate to="/login" />} />

        {/* User Role & Approvals */}
        <Route path="/user-roles" element={isAuthenticated ? <UserRoles /> : <Navigate to="/login" />} />
        <Route path="/user-roles/add-new" element={isAuthenticated ? <AddNewUser /> : <Navigate to="/login" />} />
        <Route path="/user-roles/edit/:id" element={isAuthenticated ? <EditUserRole /> : <Navigate to="/login" />} />

        {/* User Approvals */}
        <Route path="/user-approvals" element={isAuthenticated ? <UserApproval /> : <Navigate to="/login" />} />
        <Route path="/user-approvals/set-hierarchy" element={isAuthenticated ? <SetHierarchy /> : <Navigate to="/login" />} />
        <Route path="/user-approvals/edit-hierarchy/:id" element={isAuthenticated ? <EditHierarchy /> : <Navigate to="/login" />} />
        <Route path="/user-approvals/approval-requests" element={isAuthenticated ? <ApprovalRequests /> : <Navigate to="/login" />} />
        <Route path="/user-approvals/approval-tracking" element={isAuthenticated ? <ApprovalTracking /> : <Navigate to="/login" />} />

        {/* Vehicle Management Routes */}
        <Route path="/vehicle-management" element={<ProtectedRoute><VehicleManagement /></ProtectedRoute>} />
        <Route path="/vehicle-management/add-new" element={<ProtectedRoute><AddNewVehicle /></ProtectedRoute>} />
        <Route path="/vehicle-management/edit/:id" element={<ProtectedRoute><EditVehicle /></ProtectedRoute>} />
        <Route path="/vehicle-management/view/:id" element={<ProtectedRoute><ViewVehicle /></ProtectedRoute>} />
        <Route path="/vehicle-management/:vehicleId/colors" element={<ProtectedRoute><VehicleColorsManagement /></ProtectedRoute>} />
        <Route path="/vehicle-management/:vehicleId/variants" element={<ProtectedRoute><VehicleVariantsPage /></ProtectedRoute>} />
        <Route path="/vehicle-management/:vehicleId/images" element={<ProtectedRoute><VehicleImagesPage /></ProtectedRoute>} />

        {/* Wildcard route */}
        <Route path="*" element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} />} />
      </Routes>
    </>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <AppRoutes />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
      />
    </AuthProvider>
  );
};

export default App;
